import { WorkPacketType } from "src/pages/UserDashboard/WorkPackets/WorkPacketType.ts";
import { ServerFilterKeys } from "src/pages/UserDashboard/WorkPackets/Cases/casesApi.ts";
import type { CaseStatus, ShortagesSettlementsCaseStage } from "../../Cases/api/constants";

export enum CaseType {
  CHARGEBACK_ESCALATION = "CHARGEBACK_ESCALATION",
  SHORTAGE_ESCALATION = "SHORTAGE_ESCALATION",
  SHORTAGE_SETTLEMENT = "SHORTAGE_SETTLEMENT",
}

export const CaseWorkPacketTypeMap = {
  [CaseType.CHARGEBACK_ESCALATION]: WorkPacketType.CHARGEBACKS,
  [CaseType.SHORTAGE_ESCALATION]: WorkPacketType.SHORTAGES,
  [CaseType.SHORTAGE_SETTLEMENT]: WorkPacketType.SHORTAGES,
};

export const isValidCaseType = (value: string): value is CaseType => {
  return Object.values(CaseType).includes(value as CaseType);
};

export const CaseTypeLabelMap: Record<CaseType, string> = {
  [CaseType.CHARGEBACK_ESCALATION]: "Chargeback Escalation",
  [CaseType.SHORTAGE_ESCALATION]: "Shortage Escalation",
  [CaseType.SHORTAGE_SETTLEMENT]: "Shortage Settlement",
};

export interface Option {
  label?: string;
  title?: string;
  color?: string;
  value: any;
}

export const BaseCaseFields = {
  ChargeGuardCaseId: "chargeGuardCaseId",
  NumOfPackets: "numOfPackets",
  AmazonCaseId: "amazonCaseId",
  VendorName: "vendorName",
  StoreId: "storeId",
  StoreName: "storeName",
  CurrentCaseOwner: "currentCaseOwner",
  BackupCaseOwner: "backupCaseOwner",
  CurrentAction: "currentAction",
  AmazonCaseCreatedDate: "amazonCaseCreatedDate",
  CaseFilingUser: "caseFilingUser",
  ManualFilingUser: "manualFilingUser",
  RemainingOpenBalance: "remainingOpenBalance",
  CurrentCaseNumber: "currentCaseNumber",
  InvoiceStartDueDate: "invoiceStartDueDate",
  InvoiceEndDueDate: "invoiceEndDueDate",
  Link: "link",
  Payee: "payee",
  SubmissionAmt: "submissionAmt",
  SubmissionDate: "submissionDate",
} as const;

export const SettlementCaseFields = {
  ...BaseCaseFields,
  PresubmissionRequired: "presubmissionRequired",
  Business: "business",
  Sprinter: "sprinter",
  BackupSprinter: "backupSprinter",
  SprinterViewedAt: "sprinterViewedAt",
  Processor: "processor",
  BackupProcessor: "backupProcessor",
  ProcessorViewedAt: "processorViewedAt",
  VendorCodes: "vendorCodes",
  NumOfSettlements: "numOfSettlements",
  SubmissionPath: "submissionPath",
  Stage: "stage",
  AlignedBalanceAmount: "alignedBalanceAmount",
  CurrentRRCalc: "currentRRCalc",
  CurrentAmount: "currentAmount",
  ValidAmount: "validAmount",
  FinalRecoveryRateCalc: "finalRecoveryRateCalc",
} as const;

export const EscalationCaseFields = {
  ...BaseCaseFields,
  ApprovedAmount: "approvedAmount",
  ApprovedDate: "approvedDate",
  Processor: "processor",
  ReDisputeId: "disputeId",
  BackupProcessor: "backupProcessor",
  RecoveryStreamType: "recoveryStreamType",
  RecoveryStreamSubtype1: "recoveryStreamSubtype1",
  RecoveryStreamSubtype2: "recoveryStreamSubtype2",
  DisputedAmount: "disputedAmount",
  PaidAmount: "paidAmount",
  PO: "po",
  ASIN: "asin",
  MonitoringDate: "monitoringDate",
} as const;

export const CaseFields = {
  ...BaseCaseFields,
  ...SettlementCaseFields,
  ...EscalationCaseFields,
} as const;

export type CaseFieldKeys = keyof typeof CaseFields;
export type CaseFields = (typeof CaseFields)[CaseFieldKeys];

/**
 * Type for data grid values
 */
export interface BaseCase {
  [CaseFields.ChargeGuardCaseId]: string;
  [CaseFields.NumOfPackets]: number;
  [CaseFields.AmazonCaseId]: string;
  [CaseFields.VendorName]: string;
  [CaseFields.StoreId]: number;
  [CaseFields.StoreName]: string;
  [CaseFields.CurrentCaseOwner]: string;
  [CaseFields.BackupCaseOwner]: string;
  [CaseFields.CurrentAction]: CaseStatus;
  [CaseFields.AmazonCaseCreatedDate]: string;
  [CaseFields.CaseFilingUser]: string;
  [CaseFields.ManualFilingUser]: string;
  [CaseFields.RemainingOpenBalance]: number;
  [CaseFields.CurrentCaseNumber]: string;
  [CaseFields.InvoiceStartDueDate]: string;
  [CaseFields.InvoiceEndDueDate]: string;
  [CaseFields.Link]: string;
  [CaseFields.Payee]: string;
  [CaseFields.SubmissionAmt]: number;
  [CaseFields.SubmissionDate]: string;
}

export interface ShortageSettlementCase extends BaseCase {
  [CaseFields.PresubmissionRequired]: boolean;
  [CaseFields.Business]: string;
  [CaseFields.Sprinter]: string;
  [CaseFields.SprinterViewedAt]: string;
  [CaseFields.Processor]: string;
  [CaseFields.BackupProcessor]: string;
  [CaseFields.ProcessorViewedAt]: string;
  [CaseFields.VendorCodes]: string;
  [CaseFields.NumOfSettlements]: number;
  [CaseFields.SubmissionPath]: string;
  [CaseFields.Stage]: ShortagesSettlementsCaseStage;
  [CaseFields.AlignedBalanceAmount]: number;
  [CaseFields.CurrentRRCalc]: number;
  [CaseFields.CurrentAmount]: number;
  [CaseFields.ValidAmount]: number;
  [CaseFields.FinalRecoveryRateCalc]: number;
}

export interface ChargebackEscalationCase extends BaseCase {
  [EscalationCaseFields.ASIN]: string;
  [EscalationCaseFields.PO]: string;
  [EscalationCaseFields.RecoveryStreamType]: string;
  [EscalationCaseFields.RecoveryStreamSubtype1]: string;
  [EscalationCaseFields.RecoveryStreamSubtype2]: string;
  [EscalationCaseFields.ApprovedDate]: string;
  [CaseFields.ApprovedAmount]: number;
}

export interface ShortageEscalationCase extends BaseCase {
  [CaseFields.ReDisputeId]: string;
  [CaseFields.ApprovedDate]: string;
  [CaseFields.ApprovedAmount]: number;
  [CaseFields.Processor]: string;
  [CaseFields.BackupProcessor]: string;
  [CaseFields.MonitoringDate]: string;
}

export type Case = ChargebackEscalationCase | ShortageSettlementCase | ShortageEscalationCase;

/**
 * Column names for data grid
 */
export const caseColMap: Record<CaseFields, string> = {
  [CaseFields.ChargeGuardCaseId]: "CG Case ID",
  [CaseFields.AmazonCaseId]: "Amazon Case ID",
  [CaseFields.ReDisputeId]: "Re-dispute ID",
  [CaseFields.NumOfPackets]: "# of Packets",
  [CaseFields.VendorName]: "Vendor Name",
  [CaseFields.StoreId]: "Store ID",
  [CaseFields.StoreName]: "Store (Account Name)",
  [CaseFields.CurrentCaseOwner]: "Current Case Owner",
  [CaseFields.BackupCaseOwner]: "Backup Case Owner",
  [CaseFields.CurrentAction]: "Case Status",
  [CaseFields.AmazonCaseCreatedDate]: "Case Creation Date",
  [CaseFields.MonitoringDate]: "Monitoring Date",
  [CaseFields.CaseFilingUser]: "Case Filing User",
  [CaseFields.ManualFilingUser]: "Manual Filing User",
  [CaseFields.RemainingOpenBalance]: "Remaining Open Balance",
  [CaseFields.ApprovedAmount]: "Approved Amount",
  [CaseFields.ApprovedDate]: "Approved Date",
  [CaseFields.RecoveryStreamType]: "Recovery Stream Type",
  [CaseFields.RecoveryStreamSubtype1]: "Recovery Stream Subtype 1",
  [CaseFields.RecoveryStreamSubtype2]: "Recovery Stream Subtype 2",
  [CaseFields.DisputedAmount]: "Disputed Amount",
  [CaseFields.PaidAmount]: "Paid Amount",
  [CaseFields.PO]: "PO",
  [CaseFields.ASIN]: "ASIN",

  [CaseFields.PresubmissionRequired]: "Presubmission Required",
  [CaseFields.Business]: "Business",
  [CaseFields.Sprinter]: "Sprinter",
  [CaseFields.BackupSprinter]: "Backup Sprinter",
  [CaseFields.SprinterViewedAt]: "Sprinter Date",
  [CaseFields.Processor]: "Processor",
  [CaseFields.BackupProcessor]: "Backup Processor",
  [CaseFields.ProcessorViewedAt]: "Processor Date",
  [CaseFields.Payee]: "Payee",
  [CaseFields.VendorCodes]: "Vendor Codes",
  [CaseFields.NumOfSettlements]: "# Of Settlements",
  [CaseFields.InvoiceStartDueDate]: "Invoice Start Due Date",
  [CaseFields.InvoiceEndDueDate]: "Invoice End Due Date",
  [CaseFields.SubmissionPath]: "Submission Path",
  [CaseFields.CurrentCaseNumber]: "Current Case Number",
  [CaseFields.Link]: "Link",
  [CaseFields.Stage]: "Stage",
  [CaseFields.SubmissionAmt]: "Submission Amt",
  [CaseFields.SubmissionDate]: "Submission Date",
  [CaseFields.AlignedBalanceAmount]: "Aligned Balance Amount",
  [CaseFields.CurrentRRCalc]: "Current RR Calc",
  [CaseFields.CurrentAmount]: "Current Amount",
  [CaseFields.ValidAmount]: "Valid Amount",
  [CaseFields.FinalRecoveryRateCalc]: "Final Recovery Rate Calc",
} as const;

export const caseFilterLabelMap: Record<ServerFilterKeys, string> = {
  AmazonCaseId: caseColMap.amazonCaseId,
  AmazonCaseCreatedDate: caseColMap.amazonCaseCreatedDate,
  MonitoringDate: caseColMap.monitoringDate,
  CurrentAction: caseColMap.currentAction,
  CurrentCaseOwner: caseColMap.currentCaseOwner,
  BackupCaseOwner: caseColMap.backupCaseOwner,
  ParentInvoiceId: "Parent Invoice ID",
  ManualFilingUser: caseColMap.manualFilingUser,
  CaseFilingUser: caseColMap.caseFilingUser,
  PresubmissionRequired: caseColMap.presubmissionRequired,
  Processor: caseColMap.processor,
  BackupProcessor: caseColMap.backupProcessor,
  SprinterViewedAt: caseColMap.sprinterViewedAt,
  Sprinter: caseColMap.sprinter,
  BackupSprinter: caseColMap.backupSprinter,
  SubmissionAmount: caseColMap.submissionAmt,
  SubmissionDate: caseColMap.submissionDate,
  Stage: caseColMap.stage,
  StoreName: caseColMap.storeName,
  SubmissionPath: caseColMap.submissionPath,
  VendorName: caseColMap.vendorName,
  RedFlags: "Red Flags",
  CorrespondenceWorkFlags: "Correspondence Work Flags",
  VcDisputedInvoiceIds: "Last Child Invoice ID",
};

export const isShortageCaseType = (caseType: CaseType): boolean => {
  return caseType === CaseType.SHORTAGE_SETTLEMENT || caseType === CaseType.SHORTAGE_ESCALATION;
};

export const isChargebackCaseType = (caseType: CaseType): boolean => {
  return caseType === CaseType.CHARGEBACK_ESCALATION;
};

export const isEscalationCaseType = (caseType: CaseType): boolean => {
  return caseType === CaseType.SHORTAGE_ESCALATION || caseType === CaseType.CHARGEBACK_ESCALATION;
};
